(function () {
  "use strict";

  // Partners Coffee — Klaviyo sign-up popup accessibility shim.
  //
  // Reliable, cross-device announcement (iOS VoiceOver, Android TalkBack, NVDA, JAWS):
  //   1. Mute Klaviyo's own verbose aria-live region AT INSERTION (an observer armed
  //      before the popup appears) so TalkBack never reads the whole form body — the
  //      `klaviyoForms` "open" event fires too late to win that race.
  //   2. Announce the offer concisely through ONE author-controlled assertive live
  //      region — the only mechanism that fires on appearance on every screen reader
  //      (dialog focus is announced on iOS/desktop but NOT on Android touch).
  //   3. Give the dialog an accessible NAME, and move focus in STAGGERED after the
  //      announcement (a live region dropped on iOS Safari only when it fires at the
  //      same instant as a focus move — separate them in time and both are heard).
  //   4. Trap focus (Tab cycle) + Escape; restore focus to the trigger on close.

  var KLAVIYO_CONTAINER = '[class*="klaviyo-form-"]';
  var KLAVIYO_ANY =
    '[class*="klaviyo"], [class*="kl-private-reset"], [id*="klaviyo"]';
  var FOCUSABLE =
    'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, [tabindex]:not([tabindex="-1"]), [contenteditable]';
  var ANNOUNCER_ID = "patrol-kl-live";
  var OFFER_ANNOUNCEMENT = "Sign-up dialog. Enter to win free coffee.";

  var triggerElement = null;
  var lastExternalFocus = null;
  var activeDialog = null;
  var keydownHandler = null;
  var closeWatcher = null;

  function isInsideKlaviyo(node) {
    return !!(node && node.closest && node.closest(KLAVIYO_ANY));
  }

  document.addEventListener(
    "focusin",
    function (event) {
      var target = event.target;
      if (target instanceof HTMLElement && !isInsideKlaviyo(target)) {
        lastExternalFocus = target;
      }
    },
    true,
  );

  function findDialog(formId) {
    var candidates = [];
    if (formId && window.CSS && CSS.escape) {
      var byId = document.querySelector(".klaviyo-form-" + CSS.escape(formId));
      if (byId) candidates.push(byId);
    }
    var containers = document.querySelectorAll(KLAVIYO_CONTAINER);
    for (var i = 0; i < containers.length; i++) candidates.push(containers[i]);
    for (var j = 0; j < candidates.length; j++) {
      var node = candidates[j];
      if (node.getClientRects().length === 0) continue;
      var dialog =
        node.closest('[role="dialog"]') ||
        (node.matches('[role="dialog"]') ? node : null) ||
        node.querySelector('[role="dialog"]') ||
        node;
      if (dialog.getClientRects().length > 0) return dialog;
    }
    return null;
  }

  function visibleFocusables(root) {
    return Array.prototype.slice
      .call(root.querySelectorAll(FOCUSABLE))
      .filter(function (el) {
        return el.getClientRects().length > 0;
      });
  }

  function findCloseControl(root) {
    var controls = root.querySelectorAll(
      '[aria-label*="close" i], button[class*="close" i], [class*="close" i][role="button"]',
    );
    for (var i = 0; i < controls.length; i++) {
      if (controls[i].getClientRects().length > 0) return controls[i];
    }
    return null;
  }

  function teardownTrap() {
    if (keydownHandler) {
      document.removeEventListener("keydown", keydownHandler, true);
      keydownHandler = null;
    }
  }

  function waitForDialog(formId, onFound) {
    var attempts = 0;
    (function poll() {
      var dialog = findDialog(formId);
      if (dialog) {
        onFound(dialog);
        return;
      }
      if (attempts++ > 60) return;
      window.setTimeout(poll, 50);
    })();
  }

  // Give the dialog a name that conveys the offer — this is what focus announces.
  function nameTheDialog(dialog) {
    var heading = dialog.querySelector('h1, h2, h3, [role="heading"]');
    if (heading && heading.textContent.trim()) {
      if (!heading.id) heading.id = "patrol-kl-offer-heading";
      dialog.setAttribute("aria-labelledby", heading.id);
      return;
    }
    var current = dialog.getAttribute("aria-label") || "";
    if (!current || /^popup/i.test(current)) {
      dialog.setAttribute("aria-label", "Enter to win free coffee, sign-up");
    }
  }

  // Klaviyo wraps the whole form body in its own aria-live="polite" region, so on
  // insertion TalkBack reads the entire offer text (as a live update, with no role
  // context — never "dialog"). Mute every live region in the subtree EXCEPT our own
  // announcer and Klaviyo's empty role="alert" submit-status region (left live so
  // validation errors still announce). Must run at insertion to win the race.
  function muteOneLiveRegion(r) {
    if (!r || r.id === ANNOUNCER_ID) return;
    if (!r.hasAttribute("aria-live")) return;
    if (r.getAttribute("role") === "alert" && !r.textContent.trim()) return;
    r.setAttribute("aria-live", "off");
  }

  function silenceOfferLiveRegions(root) {
    if (!root || !root.querySelectorAll) return;
    muteOneLiveRegion(root);
    var regions = root.querySelectorAll("[aria-live]");
    for (var i = 0; i < regions.length; i++) muteOneLiveRegion(regions[i]);
  }

  // One persistent assertive live region we control — registered at load so a later
  // text update reliably announces across every screen reader, including on open.
  function getAnnouncer() {
    var existing = document.getElementById(ANNOUNCER_ID);
    if (existing) return existing;
    var el = document.createElement("div");
    el.id = ANNOUNCER_ID;
    el.setAttribute("role", "alert");
    el.setAttribute("aria-live", "assertive");
    el.setAttribute("aria-atomic", "true");
    el.style.cssText =
      "position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden;";
    (document.body || document.documentElement).appendChild(el);
    return el;
  }

  function announce(message) {
    var el = getAnnouncer();
    el.textContent = "";
    window.setTimeout(function () {
      el.textContent = message;
    }, 50);
  }

  // Arm BEFORE the popup appears: the instant Klaviyo inserts its subtree, mute its
  // live regions — the "open" event fires too late, after TalkBack has already read.
  function armEarlyMuteObserver() {
    var observer = new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.matches && node.matches(KLAVIYO_ANY)) {
            silenceOfferLiveRegions(node);
          } else if (node.querySelectorAll) {
            var inner = node.querySelectorAll(KLAVIYO_ANY);
            for (var k = 0; k < inner.length; k++) {
              silenceOfferLiveRegions(inner[k]);
            }
          }
        }
      }
    });
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }

  function onOpen(formId) {
    triggerElement =
      lastExternalFocus &&
      lastExternalFocus.isConnected &&
      !isInsideKlaviyo(lastExternalFocus)
        ? lastExternalFocus
        : null;

    waitForDialog(formId, function (dialog) {
      activeDialog = dialog;

      nameTheDialog(dialog);
      silenceOfferLiveRegions(dialog);

      // The single reliable on-appearance announcement — fired now, with NO focus
      // move at this instant so iOS Safari does not drop it.
      announce(OFFER_ANNOUNCEMENT);

      if (!dialog.hasAttribute("tabindex"))
        dialog.setAttribute("tabindex", "-1");

      // Focus staggered after the announcement (keyboard/trap entry, iOS/desktop
      // name), re-muting Klaviyo in case it re-rendered. Enter the modal only when
      // focus is still outside it — never yank focus from a field the user is in.
      [700, 1200].forEach(function (ms) {
        window.setTimeout(function () {
          if (activeDialog !== dialog || !dialog.isConnected) return;
          silenceOfferLiveRegions(dialog);
          if (!dialog.contains(document.activeElement)) {
            dialog.focus();
          }
        }, ms);
      });

      teardownTrap();
      keydownHandler = function (event) {
        if (!activeDialog || !activeDialog.isConnected) {
          onClose();
          return;
        }
        if (event.key === "Tab") {
          var items = visibleFocusables(activeDialog);
          if (items.length === 0) return;
          var first = items[0];
          var last = items[items.length - 1];
          var current = document.activeElement;
          if (!activeDialog.contains(current)) {
            event.preventDefault();
            first.focus();
          } else if (event.shiftKey && current === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && current === last) {
            event.preventDefault();
            first.focus();
          }
        } else if (event.key === "Escape") {
          var close = findCloseControl(activeDialog);
          if (close) close.click();
        }
      };
      document.addEventListener("keydown", keydownHandler, true);

      watchForClose();
    });
  }

  // Klaviyo fires no reliable "close" event and removes the dialog on close, so
  // the browser resets focus to the top of the page. Detect the removal directly.
  function watchForClose() {
    if (closeWatcher) window.clearInterval(closeWatcher);
    closeWatcher = window.setInterval(function () {
      if (!activeDialog) {
        window.clearInterval(closeWatcher);
        closeWatcher = null;
        return;
      }
      if (
        !activeDialog.isConnected ||
        activeDialog.getClientRects().length === 0
      ) {
        window.clearInterval(closeWatcher);
        closeWatcher = null;
        onClose();
      }
    }, 150);
  }

  function onClose() {
    teardownTrap();
    if (closeWatcher) {
      window.clearInterval(closeWatcher);
      closeWatcher = null;
    }
    var target = triggerElement;
    triggerElement = null;
    activeDialog = null;
    if (!target || !target.isConnected) return;
    // Assertively return focus to the trigger, retrying briefly to win the race
    // against the browser resetting focus to the top of the page on removal.
    var attempts = 0;
    (function restore() {
      if (target.isConnected && document.activeElement !== target) {
        try {
          target.focus();
        } catch (error) {}
      }
      if (attempts++ < 10) window.setTimeout(restore, 50);
    })();
  }

  window.addEventListener("klaviyoForms", function (event) {
    var detail = event && event.detail;
    if (!detail) return;
    if (detail.type === "open") onOpen(detail.formId);
    else if (detail.type === "close") onClose();
  });

  function init() {
    getAnnouncer();
    armEarlyMuteObserver();
    var existing = document.querySelectorAll(KLAVIYO_ANY);
    for (var i = 0; i < existing.length; i++) {
      silenceOfferLiveRegions(existing[i]);
    }
  }
  if (document.body) init();
  else document.addEventListener("DOMContentLoaded", init, { once: true });
})();

// --- Other storefront popups (nav menu, search, product options, cart) ---
// These already move focus into the dialog; the only gap is a missing accessible
// NAME on some of them. Same focus-a-named-dialog pattern: ensure each modal has a
// name so the focus-in announces a named dialog. Additive + safe: only adds a name
// when one is missing, never moves focus, and skips Klaviyo (handled above).
(function () {
  "use strict";
  var HINTS = [
    [/search/i, "Search"],
    [/navigation|menu/i, "Menu"],
    [/cart/i, "Cart"],
    [/option|variant|grind|roast|size/i, "Product options"],
  ];
  function named(d) {
    var al = d.getAttribute("aria-label");
    if (al && !/^popup/i.test(al)) return true;
    var lb = d.getAttribute("aria-labelledby");
    if (lb) {
      var ids = lb.split(/\s+/);
      for (var i = 0; i < ids.length; i++) {
        var e = document.getElementById(ids[i]);
        if (e && e.textContent.trim()) return true;
      }
    }
    return false;
  }
  function ensure(d) {
    if (!d || d.getClientRects().length === 0) return;
    if (d.closest('[class*="klaviyo"], [class*="kl-"]')) return;
    if (named(d)) return;
    var h = d.querySelector('h1, h2, h3, [role="heading"]');
    if (h && h.textContent.trim()) {
      if (!h.id) h.id = "patrol-dlg-" + Math.random().toString(36).slice(2, 8);
      d.setAttribute("aria-labelledby", h.id);
      return;
    }
    var hay = (d.id || "") + " " + (d.className || "").toString();
    for (var i = 0; i < HINTS.length; i++) {
      if (HINTS[i][0].test(hay)) {
        d.setAttribute("aria-label", HINTS[i][1]);
        return;
      }
    }
  }
  function scan() {
    var m = document.querySelectorAll('[aria-modal="true"], dialog[open]');
    for (var i = 0; i < m.length; i++) ensure(m[i]);
  }
  var pending = null;
  new MutationObserver(function () {
    if (pending) return;
    pending = window.setTimeout(function () {
      pending = null;
      scan();
    }, 120);
  }).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["aria-modal", "open"],
    childList: true,
    subtree: true,
  });
  scan();
})();
